import type { SipCommandAction } from "@/components/SIPIntegration/composables/useSipBroadcast";

export interface SipCommandInput {
  action: SipCommandAction;
  payload?: string | null;
}

export type SipCommandOp =
  | { op: "startCall"; arg: string }
  | { op: "answer" }
  | { op: "endCall" }
  | { op: "dtmf"; arg: string }
  | { op: "setPause"; arg: string }
  | { op: "focus" }
  | { op: "noop" };

export function resolveSipCommand(input: SipCommandInput): SipCommandOp {
  switch (input.action) {
    case "dial":
      return input.payload ? { op: "startCall", arg: input.payload } : { op: "noop" };
    case "answer":
      return { op: "answer" };
    case "hangup":
      return { op: "endCall" };
    case "dtmf":
      return input.payload ? { op: "dtmf", arg: input.payload } : { op: "noop" };
    case "setPause":
      return input.payload ? { op: "setPause", arg: input.payload } : { op: "noop" };
    case "focus":
      return { op: "focus" };
    default:
      return { op: "noop" };
  }
}

export interface IncomingCallPayload {
  phoneNumber: string;
}

/**
 * Normalizza il CallerID grezzo dell'INVITE nel numero da annunciare alle view.
 *
 * Non porta più un `targetUrl`: la scheda del contatto viaggia su un messaggio
 * dedicato (`incoming-lead`), perché arriva dopo lo squillo. Il campo qui era
 * cablato a `null` da sempre e dava l'illusione che l'informazione passasse
 * già alle view — è la trappola in cui è caduta GAIA-2719.
 */
export function buildIncomingCallPayload(rawUser: string | undefined): IncomingCallPayload {
  const phoneNumber = (rawUser ?? "").replace("++", "+");
  return { phoneNumber };
}

export interface OwnerCommandEffects {
  startCall: (n: string) => void;
  answer: () => void;
  endCall: () => void;
  dtmf: (t: string) => void;
  setPause: (r: string) => void;
  focus: () => void;
}

export function makeOwnerCommandHandler(fx: OwnerCommandEffects) {
  return (input: SipCommandInput): void => {
    const cmd = resolveSipCommand(input);
    switch (cmd.op) {
      case "startCall":
        fx.startCall(cmd.arg);
        break;
      case "answer":
        fx.answer();
        break;
      case "endCall":
        fx.endCall();
        break;
      case "dtmf":
        fx.dtmf(cmd.arg);
        break;
      case "setPause":
        fx.setPause(cmd.arg);
        break;
      case "focus":
        fx.focus();
        break;
      case "noop":
        break;
    }
  };
}
